/**
 * Job offer form data contract (RekruterAI) — frontend <-> backend.
 *
 * Each form section has a matching `createEmptyXxx()` function that returns an
 * object with default values (for use as Formik initialValues).
 * `createEmptyJobOffer()` composes all sections into one object sent to the backend.
 *
 * The old per-field `weight`/`categoryMaxPoints`/`scoringConfig` fields below are
 * placeholders only, from an earlier, superseded concept — no evaluation logic
 * ever reads them (see step5's still-hidden "Scoring i ranking" section). The
 * new, functional per-criterion model lives in the separate `scoring` sub-object
 * some sections carry (shared shape from `@/scoringUi`), which flows
 * into the recruitment's `localCriteria`/`scoringOverrides` at map time — see
 * jobOfferMapper.js.
 */
// Aliased: this file already defines its own (old, category-based) createEmptyScoringConfig() below.
import { createEmptyScoringConfig as createEmptyCriterionScoring } from "@/scoringUi";

// ---------------------------------------------------------------------------
// Section 1: Basic offer info
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} BasicInfo
 * @property {string} positionName
 * @property {string} department
 * @property {string} olxAdNumber
 * @property {string} companyName
 * @property {string} location
 * @property {string} workMode - enum WORK_MODE
 * @property {number|null} vacancies
 * @property {string} plannedStartDate - ISO date string, ignored when plannedStartDateIsAsap is true
 * @property {boolean} plannedStartDateIsAsap
 * @property {string} seniorityLevel - enum SENIORITY_LEVEL
 * @property {string} offerStatus - enum OFFER_STATUS
 */
export const createEmptyBasicInfo = () => ({
    positionName: "",
    department: "",
    olxAdNumber: "",
    companyName: "",
    companyDescription: "",
    location: "",
    workMode: "",
    vacancies: null,
    plannedStartDate: "",
    plannedStartDateIsAsap: false,
    seniorityLevel: "",
    offerStatus: "draft",
    sendNotifications: false,
    notificationEmails: [],
    customConsentIds: null,
    privacyPolicyEnabled: true,
});

// ---------------------------------------------------------------------------
// Section 2: Position description and knowledge base
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} PositionDescription
 * @property {string} shortDescription
 * @property {string[]} mainResponsibilities
 * @property {string[]} responsibilitiesScope
 * @property {string} positionGoals
 * @property {string} typicalWorkday
 * @property {string} teamStructure
 * @property {string} supervisor
 * @property {string[]} toolsAndSystems
 * @property {string[]} benefits
 * @property {string[]} recruitmentStages
 */
export const createEmptyPositionDescription = () => ({
    shortDescription: "",
    mainResponsibilities: [""],
    responsibilitiesScope: [],
    positionGoals: "",
    typicalWorkday: "",
    teamStructure: "",
    supervisor: "",
    employeeOnboarding: "",
    toolsAndSystems: [],
    benefits: [],
    recruitmentStages: [],
});

// ---------------------------------------------------------------------------
// Section 3: Employment form
// ---------------------------------------------------------------------------

/**
 * List item — an offer can have multiple employment forms (confirmed by PM,
 * mirrors the "Forma współpracy" / "Model wynagrodzenia" repeatable sections in Figma).
 * @typedef {Object} EmploymentForm
 * @property {string} type - enum EMPLOYMENT_FORM_TYPE
 * @property {string} preferredType - enum EMPLOYMENT_FORM_TYPE
 * @property {boolean} isEliminatingIfNotAccepted
 * @property {number} weight - placeholder, scoring not implemented yet
 * @property {string} aiQuestion
 */
export const createEmptyEmploymentForm = () => ({
    type: [],
    preferredType: "",
    isEliminatingIfNotAccepted: false,
    weight: 5,
    aiQuestion: "",
    // New, functional per-criterion scoring config (spec 2 §4). Matched to the
    // AI conversation's collected value by this item's own `aiQuestion` text
    // (required, see jobOfferMapper.js buildArrayLocalCriterionRequests) --
    // editing the question text after configuring scoring breaks the link.
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 4: Salary model and settlement
// ---------------------------------------------------------------------------

/**
 * List item — an offer can have multiple salary models (confirmed by PM,
 * mirrors the repeatable "Model wynagrodzenia" section in Figma).
 * @typedef {Object} SalaryModel
 * @property {string} type - enum SALARY_MODEL_TYPE
 * @property {string} currency - enum CURRENCY
 * @property {boolean} hasBonus
 * @property {boolean} canCommunicateToCandidate
 * @property {boolean} isNegotiable
 * @property {SalaryModelHourly|null} hourly - populated when type === "hourlyRate"
 * @property {SalaryModelFixed|null} fixedSalary - populated when type === "fixedMonthly"
 * @property {SalaryModelPieceRate|null} pieceRate - populated when type contains "pieceRate"
 * @property {SalaryModelB2B|null} b2b - populated when type === "b2b"
 */

/**
 * @typedef {Object} SalaryModelHourly
 * @property {number|null} rateFrom
 * @property {number|null} rateTo
 * @property {boolean} hasDifferentOvertimeRate
 * @property {string} overtimeDescription
 */

/**
 * @typedef {Object} SalaryModelFixed
 * @property {number|null} grossMonthlyFrom
 * @property {number|null} grossMonthlyTo
 * @property {string} isFixedDescription
 */

/**
 * @typedef {Object} SalaryModelPieceRate
 * @property {string} unit - enum PIECE_RATE_UNIT
 * @property {number|null} pricePerUnit
 * @property {string} avgUnitsDescription
 * @property {string} calculationDescription
 * @property {number|null} avgMonthlySalary
 * @property {number|null} minSalary
 * @property {number|null} maxSalary
 */

/**
 * @typedef {Object} SalaryModelB2B
 * @property {number|null} netInvoiceAmountFrom
 * @property {number|null} netInvoiceAmountTo
 * @property {string} settlementPeriod - enum SETTLEMENT_PERIOD
 * @property {string} paymentTerm
 */

export const createEmptySalaryModel = () => ({
    type: "",
    currency: "PLN",
    hasBonus: false,
    canCommunicateToCandidate: false,
    isNegotiable: false,
    hourly: null,
    fixedSalary: null,
    pieceRate: null,
    b2b: null,
});

export const createEmptySalaryModelHourly = () => ({
    rateFrom: null,
    rateTo: null,
    hasDifferentOvertimeRate: false,
    overtimeDescription: "",
});

export const createEmptySalaryModelFixed = () => ({
    grossMonthlyFrom: null,
    grossMonthlyTo: null,
    isFixedDescription: "",
});

export const createEmptySalaryModelPieceRate = () => ({
    unit: "",
    pricePerUnit: null,
    avgUnitsDescription: "",
    calculationDescription: "",
    avgMonthlySalary: null,
    minSalary: null,
    maxSalary: null,
});

export const createEmptySalaryModelB2B = () => ({
    netInvoiceAmountFrom: null,
    netInvoiceAmountTo: null,
    settlementPeriod: "",
    paymentTerm: "",
});

// ---------------------------------------------------------------------------
// Section 5: Bonuses and allowances
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} Bonus
 * @property {string} name
 * @property {string} type - enum BONUS_TYPE
 * @property {boolean} isGuaranteed
 * @property {string} calculationDescription
 * @property {number|null} amountFrom
 * @property {number|null} amountTo
 * @property {boolean} canCommunicateToCandidate
 */
export const createEmptyBonus = () => ({
    name: "",
    type: "",
    isGuaranteed: false,
    calculationDescription: "",
    amountFrom: null,
    amountTo: null,
    canCommunicateToCandidate: false,
});

/**
 * @typedef {Object} Allowance
 * @property {string} name
 * @property {string} type - enum ALLOWANCE_TYPE
 * @property {string} calculationDescription
 * @property {number|null} amountFrom
 * @property {number|null} amountTo
 * @property {boolean} canCommunicateToCandidate
 */
export const createEmptyAllowance = () => ({
    name: "",
    type: "",
    calculationDescription: "",
    amountFrom: null,
    amountTo: null,
    canCommunicateToCandidate: false,
});

// ---------------------------------------------------------------------------
// Section 6: Working time scope and organization
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} OvertimeConfig
 * @property {boolean} exists
 * @property {string} frequency - enum OVERTIME_FREQUENCY
 * @property {boolean} isMandatory
 * @property {string} settlementMethod - enum OVERTIME_SETTLEMENT
 * @property {boolean} refusalIsEliminating
 */
export const createEmptyOvertimeConfig = () => ({
    exists: false,
    frequency: "",
    isMandatory: false,
    settlementMethod: "",
    refusalIsEliminating: false,
});

/**
 * @typedef {Object} WorkingTime
 * @property {string} workloadType - enum WORKLOAD_TYPE
 * @property {number|null} minHoursPerWeek
 * @property {number|null} maxHoursPerWeek
 * @property {string} workSystem - enum WORK_SYSTEM
 * @property {string} startTime - "HH:mm"
 * @property {string} endTime - "HH:mm"
 * @property {boolean} isFixedSchedule
 * @property {boolean} isFlexibleSchedule
 * @property {boolean} worksWeekends
 * @property {boolean} worksHolidays
 * @property {boolean} hasBusinessTrips
 * @property {boolean} worksNightShifts
 * @property {boolean} hasOnCallDuty
 * @property {OvertimeConfig} overtime
 */
export const createEmptyWorkingTime = () => ({
    workloadType: "",
    minHoursPerWeek: null,
    maxHoursPerWeek: null,
    workSystem: "",
    startTime: "",
    endTime: "",
    isFixedSchedule: false,
    isFlexibleSchedule: false,
    worksWeekends: false,
    worksHolidays: false,
    hasBusinessTrips: false,
    worksNightShifts: false,
    hasOnCallDuty: false,
    overtime: createEmptyOvertimeConfig(),
});

// ---------------------------------------------------------------------------
// Section 7: Safety and working conditions
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} WorkConditions
 * @property {boolean} physicalWork
 * @property {boolean} workingAtHeight
 * @property {boolean} workingWithMachinery
 * @property {boolean} nightWork
 * @property {boolean} outdoorWork
 * @property {boolean} drivingVehicles
 * @property {boolean} chemicalSubstancesContact
 * @property {boolean} noiseExposure
 * @property {boolean} dustExposure
 * @property {boolean} customerContact
 * @property {string} other
 */
export const createEmptyWorkConditions = () => ({
    physicalWork: false,
    workingAtHeight: false,
    workingWithMachinery: false,
    nightWork: false,
    outdoorWork: false,
    drivingVehicles: false,
    chemicalSubstancesContact: false,
    noiseExposure: false,
    dustExposure: false,
    customerContact: false,
    other: "",
});

/**
 * @typedef {Object} RiskFactor
 * @property {string} name
 * @property {string} description
 * @property {boolean} aiShouldAsk
 * @property {boolean} refusalIsEliminating
 * @property {number} weight - placeholder, scoring not implemented yet
 */
export const createEmptyRiskFactor = () => ({
    name: "",
    description: "",
    aiShouldAsk: false,
    refusalIsEliminating: false,
    weight: 0,
    // New, functional per-criterion scoring config (spec 2 §4). Matched to the
    // AI conversation's collected value by this item's own `name` (required)
    // -- editing the name after configuring scoring breaks the link, see
    // jobOfferMapper.js buildArrayLocalCriterionRequests.
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 8: Personal protective equipment
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} ProtectiveEquipment
 * @property {string} name
 * @property {boolean} providedByEmployer
 * @property {boolean} requiredFromEmployee
 * @property {boolean} mandatoryForPosition
 * @property {boolean} refusalIsEliminating
 * @property {number} weight - placeholder, scoring not implemented yet
 * @property {string} aiQuestion
 */
export const createEmptyProtectiveEquipment = () => ({
    name: "",
    providedByEmployer: false,
    requiredFromEmployee: false,
    mandatoryForPosition: false,
    refusalIsEliminating: false,
    weight: 0,
    aiQuestion: "",
});

// ---------------------------------------------------------------------------
// Legacy scoring model — shared by documents, skills and experience
// requirements. Its fields (isRequired/isCritical/weight/askCandidate/...)
// are never read by any evaluation logic; kept only as `legacyScoring` so the
// backend's `ScoringCriteria` value object (still `NotNull()`-validated on
// these three) keeps receiving a well-formed, inert payload. The real,
// functional per-criterion config now lives in `scoring` (shared shape from
// `@/scoringUi`), same as every other scoring-capable section — see
// jobOfferMapper.js.
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} LegacyScoringFields
 * @property {boolean} isRequired
 * @property {boolean} isCritical
 * @property {boolean} isEliminating
 * @property {number} weight
 * @property {string|number|null} minAcceptedValue
 * @property {string|number|null} preferredValue
 * @property {boolean} askCandidate
 * @property {string} questionText
 * @property {string} followUpQuestionText
 * @property {string} internalComment
 */
export const createEmptyScoringFields = () => ({
    isRequired: false,
    isCritical: false,
    isEliminating: false,
    weight: 0,
    minAcceptedValue: null,
    preferredValue: null,
    askCandidate: false,
    questionText: "",
    followUpQuestionText: "",
    internalComment: "",
});

// ---------------------------------------------------------------------------
// Section 9: Documents, licenses and certifications
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} RequiredDocument
 * @property {string} name
 * @property {string} documentType - enum DOCUMENT_TYPE
 * @property {string} status - enum REQUIREMENT_STATUS
 * @property {string} criticality - enum CRITICALITY
 * @property {boolean} requiresUpload
 * @property {boolean} mustBeCurrent
 * @property {LegacyScoringFields} legacyScoring - inert, see note above
 * @property {import("@/scoringUi").ScoringConfig} scoring - matched by this item's own `name` (see jobOfferMapper.js)
 */
export const createEmptyRequiredDocument = () => ({
    name: "",
    documentType: "",
    status: "",
    criticality: "",
    requiresUpload: false,
    mustBeCurrent: false,
    additionalInfo: "",
    legacyScoring: createEmptyScoringFields(),
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 10: Skills and competencies
// ---------------------------------------------------------------------------

/**
 * Confirmed by PM: `ratingScale` is the enum that tells the bot which kind of
 * scale to use for this skill; `minLevel`'s value type follows it (number for
 * scale1to5/scale1to10, word for levelWords, bool for yesNo).
 * @typedef {Object} Skill
 * @property {string} name
 * @property {string} category - enum SKILL_CATEGORY
 * @property {string} status - enum REQUIREMENT_STATUS
 * @property {string} criticality - enum CRITICALITY
 * @property {string} ratingScale - enum RATING_SCALE
 * @property {string|number|null} minLevel
 * @property {boolean} aiAsksLevel
 * @property {boolean} requiresConfirmation
 * @property {string} confirmationType - enum CONFIRMATION_TYPE
 * @property {LegacyScoringFields} legacyScoring - inert, see note above
 * @property {import("@/scoringUi").ScoringConfig} scoring - matched by this item's own `name` (see jobOfferMapper.js)
 */
export const createEmptySkill = () => ({
    name: "",
    category: "",
    status: "",
    criticality: "",
    ratingScale: "",
    minLevel: null,
    aiAsksLevel: false,
    requiresConfirmation: false,
    confirmationType: "",
    additionalInfo: "",
    legacyScoring: createEmptyScoringFields(),
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 11: Work experience
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} ExperienceRequirement
 * @property {string} area
 * @property {number|null} minYears
 * @property {string} status - enum REQUIREMENT_STATUS
 * @property {LegacyScoringFields} legacyScoring - inert, see note above
 * @property {import("@/scoringUi").ScoringConfig} scoring - matched by this item's own `area` (see jobOfferMapper.js)
 */
export const createEmptyExperienceRequirement = () => ({
    area: "",
    minYears: null,
    status: "",
    legacyScoring: createEmptyScoringFields(),
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 12: Custom client questions
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} CustomQuestion
 * @property {string} questionText
 * @property {string} answerType - enum CUSTOM_QUESTION_ANSWER_TYPE
 * @property {boolean} isRequired
 * @property {boolean} isEliminating
 * @property {string} expectedAnswer
 * @property {string} eliminatingAnswer
 * @property {number} weight - placeholder, scoring not implemented yet
 * @property {boolean} aiCanFollowUp
 * @property {string} followUpQuestionText
 */
export const createEmptyCustomQuestion = () => ({
    questionText: "",
    answerType: "",
    isRequired: false,
    isEliminating: false,
    expectedAnswer: "",
    eliminatingAnswer: "",
    weight: 0,
    aiCanFollowUp: false,
    followUpQuestionText: "",
    // New, functional per-criterion scoring config (spec 2 §4). Matched to the
    // AI conversation's collected value by this item's own `questionText`
    // (required) -- editing the question text after configuring scoring
    // breaks the link, see jobOfferMapper.js buildArrayLocalCriterionRequests.
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 13: Motivation and fit
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} MotivationQuestion
 * @property {string} questionText
 * @property {string} expectedAnswer
 * @property {string} positiveSignals
 * @property {string} riskSignals
 * @property {number} weight - placeholder, scoring not implemented yet
 * @property {boolean} canAnswerEliminate
 */
export const createEmptyMotivationQuestion = () => ({
    questionText: "",
    expectedAnswer: "",
    positiveSignals: "",
    riskSignals: "",
    weight: 0,
    canAnswerEliminate: false,
    // New, functional per-criterion scoring config (spec 2 §4). Matched to the
    // AI conversation's collected value by this item's own `questionText`
    // (required) -- editing the question text after configuring scoring
    // breaks the link, see jobOfferMapper.js buildArrayLocalCriterionRequests.
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 14: Candidate financial expectations
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} FinancialExpectations
 * @property {boolean} aiAsksAboutExpectations
 * @property {number|null} acceptableRangeFrom
 * @property {number|null} acceptableRangeTo
 * @property {number|null} preferredRangeFrom
 * @property {number|null} preferredRangeTo
 * @property {number|null} eliminatingAbove
 * @property {string} currency - enum CURRENCY
 * @property {boolean} exceedingBudgetIsEliminating
 * @property {number} weight - placeholder, scoring not implemented yet
 */
export const createEmptyFinancialExpectations = () => ({
    aiAsksAboutExpectations: false,
    acceptableRangeFrom: null,
    acceptableRangeTo: null,
    preferredRangeFrom: null,
    preferredRangeTo: null,
    eliminatingAbove: null,
    currency: "PLN",
    exceedingBudgetIsEliminating: false,
    weight: 0,
    // New, functional per-criterion scoring config (spec 2 §4) -- see the
    // same note on createEmptyAvailability() above and LOCAL_CRITERION_NAMES
    // in jobOfferMapper.js.
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 15: Candidate availability
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} Availability
 * @property {string} earliestAcceptedStartDate - ISO date string
 * @property {number|null} maxNoticePeriodDays
 * @property {boolean} aiAsksAboutAvailability
 * @property {boolean} lackOfQuickAvailabilityIsEliminating
 * @property {number} weight - placeholder, scoring not implemented yet
 * @property {boolean} requiresShiftWorkReadiness
 * @property {boolean} requiresWeekendReadiness
 * @property {boolean} requiresBusinessTripReadiness
 * @property {boolean} requiresOvertimeReadiness
 */
export const createEmptyAvailability = () => ({
    earliestAcceptedStartDate: "",
    maxNoticePeriodDays: null,
    aiAsksAboutAvailability: false,
    lackOfQuickAvailabilityIsEliminating: false,
    weight: 0,
    requiresShiftWorkReadiness: false,
    requiresWeekendReadiness: false,
    requiresBusinessTripReadiness: false,
    requiresOvertimeReadiness: false,
    // New, functional per-criterion scoring config (spec 2 §4) -- flows into
    // Recruitment.LocalCriteria at map time under a well-known name, see
    // LOCAL_CRITERION_NAMES in jobOfferMapper.js. Distinct from the inert
    // `weight` field above.
    scoring: { ...createEmptyCriterionScoring(), criterionKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) },
});

// ---------------------------------------------------------------------------
// Section 16: Disqualification criteria
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} DisqualificationRule
 * @property {string} name
 * @property {string} conditionDescription
 * @property {string} relatedFormField - free-text string for now (confirmed by PM,
 *   no field registry / relation logic yet — built later)
 * @property {string} eliminatingValue
 * @property {boolean} isAutomaticElimination
 * @property {boolean} requiresManualDecision
 * @property {string} recruiterComment
 */
export const createEmptyDisqualificationRule = () => ({
    name: "",
    conditionDescription: "",
    relatedFormField: "",
    eliminatingValue: "",
    isAutomaticElimination: false,
    requiresManualDecision: false,
    recruiterComment: "",
});

// ---------------------------------------------------------------------------
// Section 17: RekruterAI conversation configuration
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} AiConversationConfig
 * @property {string} conversationStyle - enum CONVERSATION_STYLE
 * @property {string[]} languages - enum CONVERSATION_LANGUAGE
 * @property {boolean} canAnswerAboutSalary
 * @property {boolean} canAnswerAboutBenefits
 * @property {boolean} canAnswerAboutCompany
 * @property {string} confidentialInfo
 * @property {string} fallbackAnswer
 * @property {number|null} maxFollowUpQuestions
 */
export const createEmptyAiConversationConfig = () => ({
    conversationStyle: "",
    languages: ["pl"],
    canAnswerAboutSalary: false,
    canAnswerAboutBenefits: false,
    canAnswerAboutCompany: false,
    confidentialInfo: "",
    fallbackAnswer: "",
    maxFollowUpQuestions: null,
});

// ---------------------------------------------------------------------------
// Section 18: Scoring and ranking
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} CategoryMaxPoints
 * @property {number} documents
 * @property {number} criticalSkills
 * @property {number} optionalSkills
 * @property {number} experience
 * @property {number} availability
 * @property {number} financialExpectations
 * @property {number} motivation
 * @property {number} safety
 * @property {number} overtimeAndShifts
 * @property {number} customQuestions
 */
export const createEmptyCategoryMaxPoints = () => ({
    documents: 20,
    criticalSkills: 30,
    optionalSkills: 10,
    experience: 20,
    availability: 10,
    financialExpectations: 10,
    motivation: 10,
    safety: 15,
    overtimeAndShifts: 10,
    customQuestions: 0, // "per configuration" — no default value given in the spec
});

/**
 * Confirmed by PM: scoring/weights/thresholds are dropped for now — kept as
 * placeholder fields only, no scoring logic built yet.
 * @typedef {Object} ScoringConfig
 * @property {number|null} maxTotalPoints - placeholder
 * @property {number|null} veryGoodMatchThreshold - placeholder
 * @property {number|null} goodMatchThreshold - placeholder
 * @property {number|null} partialMatchThreshold - placeholder
 * @property {boolean} eliminationBlocksRanking - placeholder
 * @property {CategoryMaxPoints} categoryMaxPoints - placeholder
 */
export const createEmptyScoringConfig = () => ({
    maxTotalPoints: null,
    veryGoodMatchThreshold: null,
    goodMatchThreshold: null,
    partialMatchThreshold: null,
    eliminationBlocksRanking: true,
    categoryMaxPoints: createEmptyCategoryMaxPoints(),
});

// ---------------------------------------------------------------------------
// Section-level "Additional information" free-text notes (one per form section).
// Keys mirror the backend SectionAdditionalInfo record.
// ---------------------------------------------------------------------------

export const createEmptySectionAdditionalInfo = () => ({
    basicInfo: "",
    positionDescription: "",
    employmentForms: "",
    salaryModels: "",
    bonusesAndAllowances: "",
    workingTime: "",
    safetyAndConditions: "",
    protectiveEquipment: "",
    motivation: "",
    financialExpectations: "",
    availability: "",
    experience: "",
});

// ---------------------------------------------------------------------------
// Full form — JobOffer (contract sent to the backend)
// ---------------------------------------------------------------------------

/**
 * Sections that, in the MVP, can be hidden for offers they don't apply to
 * (safety, protective equipment, bonuses, allowances, risk factors — see spec "MVP scope").
 * Confirmed by PM: UI-only hide flag, no data removal / exclusion logic.
 * @typedef {"bonusesAndAllowances"|"safety"|"protectiveEquipment"|"riskFactors"} HideableSection
 */

/**
 * @typedef {Object} JobOffer
 * @property {string|null} id
 * @property {BasicInfo} basicInfo
 * @property {PositionDescription} positionDescription
 * @property {EmploymentForm[]} employmentForms
 * @property {SalaryModel[]} salaryModels
 * @property {Bonus[]} bonuses
 * @property {Allowance[]} allowances
 * @property {WorkingTime} workingTime
 * @property {WorkConditions} workConditions
 * @property {RiskFactor[]} riskFactors
 * @property {ProtectiveEquipment[]} protectiveEquipment
 * @property {RequiredDocument[]} requiredDocuments
 * @property {Skill[]} skills
 * @property {ExperienceRequirement[]} experienceRequirements
 * @property {CustomQuestion[]} customQuestions
 * @property {MotivationQuestion[]} motivationQuestions
 * @property {FinancialExpectations} financialExpectations
 * @property {Availability} availability
 * @property {DisqualificationRule[]} disqualificationRules
 * @property {AiConversationConfig} aiConversationConfig
 * @property {ScoringConfig} scoringConfig
 * @property {HideableSection[]} hiddenSections
 */
export const createEmptyJobOffer = () => ({
    id: null,
    scoringEnabled: false,
    basicInfo: createEmptyBasicInfo(),
    positionDescription: createEmptyPositionDescription(),
    employmentForms: [createEmptyEmploymentForm()],
    salaryModels: [createEmptySalaryModel()],
    bonuses: [],
    allowances: [],
    workingTime: createEmptyWorkingTime(),
    workConditions: createEmptyWorkConditions(),
    riskFactors: [],
    protectiveEquipment: [],
    requiredDocuments: [createEmptyRequiredDocument()],
    skills: [createEmptySkill()],
    experienceRequirements: [],
    customQuestions: [],
    motivationQuestions: [],
    financialExpectations: createEmptyFinancialExpectations(),
    availability: createEmptyAvailability(),
    disqualificationRules: [],
    aiConversationConfig: createEmptyAiConversationConfig(),
    scoringConfig: createEmptyScoringConfig(),
    sectionAdditionalInfo: createEmptySectionAdditionalInfo(),
    hiddenSections: [],
    // Recruitment-side per-criterion scoring inheritance (spec 2 §5): overrides
    // of a scenario-defined criterion, plus criteria local to this recruitment
    // (e.g. from scoring-capable form sections -- see jobOfferMapper.js).
    scoringOverrides: [],
    localCriteria: [],
});

// ---------------------------------------------------------------------------
// "Post-conversation report" section (CandidateReport) — backend response contract,
// not part of the offer form itself, but described in the same spec.
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} CandidateReport
 * @property {string} candidateId - relation
 * @property {string} jobOfferId - relation
 * @property {number} scorePoints
 * @property {number} scorePercentage
 * @property {string} recommendationStatus - enum RECOMMENDATION_STATUS
 * @property {boolean} hadEliminatingCriteria
 * @property {string[]} eliminatingCriteriaList
 * @property {string[]} strengths
 * @property {string[]} risks
 * @property {string[]} missingDocuments
 * @property {string[]} missingSkills
 * @property {string} financialExpectations
 * @property {string} availability
 * @property {Array<{question: string, answer: string}>} customQuestionAnswers
 * @property {string} recommendationJustification
 */
export const createEmptyCandidateReport = () => ({
    candidateId: "",
    jobOfferId: "",
    scorePoints: 0,
    scorePercentage: 0,
    recommendationStatus: "",
    hadEliminatingCriteria: false,
    eliminatingCriteriaList: [],
    strengths: [],
    risks: [],
    missingDocuments: [],
    missingSkills: [],
    financialExpectations: "",
    availability: "",
    customQuestionAnswers: [],
    recommendationJustification: "",
});
